/**
 * 생성기 대기 카드 — 기존 문제 목록 끝에 붙는 N장의 자리. 순수 UI 다. Figma `.AI 생성/대기 카드`.
 * 폼 값이 아니라 화면에만 있는 자리라 저장·dirty 에 섞이지 않는다(호출부가 목록 밖에서 그린다).
 * - 만드는 중: 워시 + 그라디언트 「문제를 생성 중입니다」(M5, 유사 문제 대기 카드와 같은 로딩).
 *   유형 배지·삭제는 결과가 오기 전이라 꺼져 있다. 취소는 생성 중 토스트의 「중단」이 맡는다.
 * - 실패: ⓘ 「문제를 만들지 못했습니다」 + 「다시 시도」. 🗑 로 그 자리를 걷는다.
 */
import { IconButton } from '@zep-us/zus';
import { Button } from '@zep-us/zus/button';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { ExclamationmarkSmallCircleIcon, MenuSmallIcon, SparklingFillIcon, TrashIcon } from '@zep-us/zus-icons';
import { CaretDownIcon } from '@/icons';
import { cn } from '@/utils/tailwind.util';
import { QuizCard } from '../../../components/EditableQuizCard/QuizCardUI';
import { QuizTypeSelectorButton } from '../../../components/EditableQuizCard/QuizTypeSelector/QuizTypeSelector';
import { SimilarQuestionWash } from '../../question-card-ai/components/SimilarQuestionWash';
import { type AiGenerateDockPendingCardsCopy } from '../types/aiGenerateDock.types';
import { type AiBuilderQuizTypeHint } from '../utils/types';
export type AiGenerateDockPendingCardsUIProps = {
  count: number;
  /** 첫 대기 카드의 문항 번호(1부터) — 기존 문제 수 + 1. */
  startNumber: number;
  /** 헤더 유형 배지 — 요청한 유형 중 첫 번째. */
  quizType: AiBuilderQuizTypeHint;
  copy: AiGenerateDockPendingCardsCopy;
  status?: 'generating' | 'failed';
  onRetry?: () => void;
  /** 실패한 자리 하나를 걷는다(index 는 0부터). */
  onDismiss?: (index: number) => void;
};
const GeneratingBody = ({
  copy,
  hidden
}: {
  copy: AiGenerateDockPendingCardsCopy;
  hidden: boolean;
}) => <>
    <SimilarQuestionWash data-sentry-element="SimilarQuestionWash" data-sentry-source-file="AiGenerateDockPendingCardsUI.tsx" />
    {/* 색은 유사 문제 대기 카드와 같은 핸드오프 raw 값 — 그라디언트 텍스트 토큰은 디자이너 확인 대기. */}
    <SparklingFillIcon size="md" fill="#51A4D4" aria-hidden className="relative" data-sentry-element="SparklingFillIcon" data-sentry-source-file="AiGenerateDockPendingCardsUI.tsx" />
    <p className="relative bg-[linear-gradient(165deg,#8478FF_13%,#18D0B4_97%)] bg-clip-text text-body-lg-semibold text-transparent" aria-hidden={hidden || undefined}>
      {copy.generating}
    </p>
  </>;
const FailedBody = ({
  copy,
  onRetry
}: {
  copy: AiGenerateDockPendingCardsCopy;
  onRetry?: () => void;
}) => <>
    <ExclamationmarkSmallCircleIcon size="md" fill={ICON_COLOR.alternative} aria-hidden data-sentry-element="ExclamationmarkSmallCircleIcon" data-sentry-source-file="AiGenerateDockPendingCardsUI.tsx" />
    <p className="text-body-lg-semibold text-assistive">{copy.failed}</p>
    <Button size="sm" variant="outlined" intent="neutral" data-testid="ai-generate-dock-pending-retry" onClick={onRetry} data-sentry-element="Button" data-sentry-source-file="AiGenerateDockPendingCardsUI.tsx">
      {copy.retry}
    </Button>
  </>;
export const AiGenerateDockPendingCardsUI = ({
  count,
  startNumber,
  quizType,
  copy,
  status = 'generating',
  onRetry,
  onDismiss
}: AiGenerateDockPendingCardsUIProps) => {
  const failed = status === 'failed';
  return <div data-testid="ai-generate-dock-pending-cards" data-state={status} aria-busy={!failed} aria-live="polite" className="flex w-full flex-col items-center gap-sm" data-sentry-component="AiGenerateDockPendingCardsUI" data-sentry-source-file="AiGenerateDockPendingCardsUI.tsx">
      {Array.from({
      length: count
    }, (_, index) => <QuizCard.Root key={index} data-testid="ai-generate-dock-pending-card" className="w-full max-w-[650px]">
          {/* 드래그 핸들 모양만 — 결과가 오기 전엔 옮길 수 없다. */}
          <div className="flex w-full justify-center py-3xs" aria-hidden>
            <MenuSmallIcon size="sm" className="rotate-90" fill={ICON_COLOR.alternative} />
          </div>
          <QuizCard.Header className="justify-between">
            <div className="flex items-center gap-sm">
              <span className="text-heading-sm-bold text-default">
                {copy.questionNumber(startNumber + index)}
              </span>
              <QuizTypeSelectorButton selectedType={quizType} disabled className="opacity-64" rightSlot={<CaretDownIcon size="xs" />} />
            </div>
            <IconButton size="xs" variant="outlined" intent="neutral" aria-label={copy.dismiss} data-testid="ai-generate-dock-pending-dismiss" disabled={!failed} onClick={() => onDismiss?.(index)}>
              <TrashIcon size="xs" fill={ICON_COLOR.neutral} />
            </IconButton>
          </QuizCard.Header>
          <QuizCard.Content className="pt-lg">
            <div className={cn('relative flex h-[180px] w-full flex-col items-center justify-center gap-xs overflow-hidden rounded-2xs', failed ? 'bg-[linear-gradient(117deg,theme(colors.white),theme(colors.gray.200))]' : 'bg-default')}>
              {failed ? <FailedBody copy={copy} onRetry={onRetry} /> : <GeneratingBody copy={copy} hidden={index > 0} />}
            </div>
          </QuizCard.Content>
        </QuizCard.Root>)}
    </div>;
};